import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Button, Image, Spinner, Modal, Form, Row, Col } from 'react-bootstrap';
import swal from 'sweetalert';

const ManageProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [loadingProduct, setLoadingProduct] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        emission: '',
        displacement: '',
        engine: '',
        cylinders: '',
        milage: '',
        price: '',
        description: '',
        maxPower: '',
        maxTorque: '',
        fuelCapacity: '',
        seatHeight: '',
        weight: '',
        gear: '',
        img: '',
        model3d: ''
    });

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = () => {
        setLoading(true);
        axios.get('https://bikezone-server.onrender.com/products')
        .then(({ data }) => {
            setProducts(data);
            setLoading(false);
        })
        .catch(error => {
            console.error(error);
            setLoading(false);
            swal({
                title: "Error!",
                text: "Failed to load products",
                icon: "error",
                button: "OK",
            });
        });
    };

    const handleUpdate = async (product) => {
        setSelectedProduct(product);
        setShowUpdateModal(true);
        setLoadingProduct(true);
        
        try {
            // Fetch fresh product data from backend
            const { data } = await axios.get(`https://bikezone-server.onrender.com/products/${product._id}`);
            
            setFormData({
                title: data?.title || '',
                emission: data?.emission || '',
                displacement: data?.displacement || '',
                engine: data?.engine || '',
                cylinders: data?.cylinders || '',
                milage: data?.milage || '',
                price: data?.price || '',
                description: data?.description || '',
                maxPower: data?.max_power || '',
                maxTorque: data?.max_torque || '',
                fuelCapacity: data?.fuel_capacity || '',
                seatHeight: data?.seatHeight || '',
                weight: data?.weight || '',
                gear: data?.gear || '',
                img: data?.img || '',
                model3d: data?.model3d || ''
            });
        } catch (error) {
            console.error('Error fetching product:', error);
            swal({
                title: "Error!",
                text: "Failed to load product details",
                icon: "error",
                button: "OK",
            });
            setShowUpdateModal(false);
        } finally {
            setLoadingProduct(false);
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);

        try {
            // Prepare the data to send - include _id in body as backend expects
            const updateData = {
                _id: selectedProduct._id, // Backend expects _id in the body!
                title: formData.title,
                emission: formData.emission,
                displacement: formData.displacement,
                engine: formData.engine,
                cylinders: formData.cylinders,
                milage: formData.milage,
                price: formData.price,
                description: formData.description,
                max_power: formData.maxPower,
                max_torque: formData.maxTorque,
                fuel_capacity: formData.fuelCapacity,
                seatHeight: formData.seatHeight,
                weight: formData.weight,
                gear: formData.gear,
                img: formData.img,
                model3d: formData.model3d
            };

            console.log('Updating product:', selectedProduct._id);
            console.log('Update data:', updateData);

            const { data } = await axios.put(
                `https://bikezone-server.onrender.com/products`,
                updateData
            );

            console.log('Update response:', data);

            if (data.modifiedCount > 0 || data.matchedCount > 0 || data.acknowledged) {
                swal({
                    title: "Success!",
                    text: "Product updated successfully",
                    icon: "success",
                    button: "OK",
                });
                
                // Refresh products list from backend
                fetchProducts();
                
                setShowUpdateModal(false);
                setSelectedProduct(null);
            } else {
                swal({
                    title: "No Changes",
                    text: "No changes were made to the product",
                    icon: "info",
                    button: "OK",
                });
            }
        } catch (error) {
            console.error('Update error:', error);
            console.error('Error response:', error.response);
            
            // More detailed error message
            const errorMessage = error.response?.data?.message 
                || error.response?.data?.error 
                || error.message 
                || "Failed to update product";
            
            swal({
                title: "Error!",
                text: `${errorMessage}\n\nStatus: ${error.response?.status || 'Unknown'}`,
                icon: "error",
                button: "OK",
            });
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = (id) => {
        swal({
            title: "Are you sure?",
            text: "This product will be permanently deleted!",
            icon: "warning",
            dangerMode: true,
            buttons: ["Cancel", "Delete"],
        }).then(result => {
            if(result){
                axios.delete(`https://bikezone-server.onrender.com/products/${id}`)
                .then(({ data }) => {
                    if(data.deletedCount === 1){
                        swal({
                            title: "Deleted!",
                            text: "Product has been deleted successfully",
                            icon: "success",
                            button: "OK",
                        });
                        const filtered = products.filter(product => product._id !== id);
                        setProducts(filtered);
                    }
                    else{
                        swal({
                            title: "Failed!",
                            text: "Something went wrong",
                            icon: "error",
                            button: "OK",
                        });
                    }
                });
            }
        });
    }

    const filteredProducts = products.filter(product => 
        product?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product?.emission?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product?.price?.toString().includes(searchTerm)
    );

    return (
        <>
            <div style={{ padding: '30px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
                {/* Header Section */}
                <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '25px 30px',
                    marginBottom: '25px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                        <div>
                            <h2 style={{ margin: '0', fontSize: '28px', fontWeight: '700', color: '#2c3e50' }}>
                                Manage Products
                            </h2>
                            <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
                                Total Products: <span style={{ fontWeight: '600', color: '#495057' }}>{filteredProducts.length}</span>
                            </p>
                        </div>
                        
                        {/* Search Bar */}
                        <div style={{ position: 'relative', width: '300px' }}>
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 40px 10px 15px',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6',
                                    fontSize: '14px',
                                    transition: 'all 0.3s ease'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                            />
                            <span style={{
                                position: 'absolute',
                                right: '15px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '18px'
                            }}>
                                🔍
                            </span>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <Spinner animation="border" variant="primary" />
                        <p style={{ marginTop: '15px', color: '#6c757d' }}>Loading products...</p>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '60px',
                        textAlign: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '15px' }}>📦</div>
                        <h3 style={{ color: '#6c757d', fontWeight: '500' }}>No products found</h3>
                        <p style={{ color: '#adb5bd' }}>Try adjusting your search</p>
                    </div>
                ) : (
                    /* Products Table */
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                        <th style={{ padding: '18px 20px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                                            Product Name
                                        </th>
                                        <th style={{ padding: '18px 20px', textAlign: 'center', fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                                            Emission
                                        </th>
                                        <th style={{ padding: '18px 20px', textAlign: 'center', fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                                            Engine
                                        </th>
                                        <th style={{ padding: '18px 20px', textAlign: 'center', fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                                            Price
                                        </th>
                                        <th style={{ padding: '18px 20px', textAlign: 'center', fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.map(product => (
                                        <tr 
                                            key={product?._id}
                                            style={{ 
                                                borderBottom: '1px solid #e9ecef',
                                                transition: 'background 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                        >
                                            <td style={{ padding: '20px', fontSize: '14px', color: '#2c3e50', fontWeight: '500' }}>
                                                {product?.title}
                                            </td>
                                            <td style={{ padding: '20px', textAlign: 'center', fontSize: '14px', color: '#6c757d' }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    background: '#e3f2fd',
                                                    color: '#1976d2',
                                                    fontSize: '12px',
                                                    fontWeight: '500'
                                                }}>
                                                    {product?.emission || 'N/A'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '20px', textAlign: 'center', fontSize: '14px', color: '#6c757d' }}>
                                                {product?.displacement || 'N/A'}
                                            </td>
                                            <td style={{ padding: '20px', textAlign: 'center', fontSize: '16px', fontWeight: '600', color: '#28a745' }}>
                                                ৳{product?.price?.toLocaleString()}
                                            </td>
                                            <td style={{ padding: '20px', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center' }}>
                                                    <Button
                                                        onClick={() => handleUpdate(product)}
                                                        style={{
                                                            background: '#e3f2fd',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            padding: '8px 12px',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.background = '#2196f3';
                                                            e.target.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.background = '#e3f2fd';
                                                            e.target.style.transform = 'scale(1)';
                                                        }}
                                                        title="Edit Product"
                                                    >
                                                        <Image 
                                                            src='https://i.ibb.co/kmZWhGK/1601884.png' 
                                                            width='20'
                                                            alt='Edit'
                                                        />
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDelete(product?._id)}
                                                        style={{
                                                            background: '#ffebee',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            padding: '8px 12px',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.background = '#ef5350';
                                                            e.target.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.background = '#ffebee';
                                                            e.target.style.transform = 'scale(1)';
                                                        }}
                                                        title="Delete Product"
                                                    >
                                                        <Image 
                                                            src='https://i.ibb.co/jDqXJSc/3221897.png' 
                                                            width='20'
                                                            alt='Delete'
                                                        />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Update Product Modal */}
            <Modal
                show={showUpdateModal}
                onHide={() => {
                    setShowUpdateModal(false);
                    setSelectedProduct(null);
                }}
                size="lg"
                centered
            >
                <Modal.Header 
                    closeButton 
                    style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none'
                    }}
                >
                    <Modal.Title style={{ fontWeight: '700' }}>
                        ✏️ Update Product
                    </Modal.Title>
                </Modal.Header>
                
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body style={{ padding: '30px' }}>
                        {loadingProduct ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Spinner animation="border" variant="primary" />
                                <p style={{ marginTop: '15px', color: '#6c757d' }}>Loading product details...</p>
                            </div>
                        ) : (
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Engine
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="engine"
                                            value={formData.engine}
                                            onChange={handleInputChange}
                                            placeholder="e.g., Air cooled, 4 Stroke 2 Valve"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Cylinders
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cylinders"
                                            value={formData.cylinders}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 1"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Mileage (km/l)
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="milage"
                                            value={formData.milage}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 40.6"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Gear
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="gear"
                                            value={formData.gear}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 5"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Image URL
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="img"
                                            value={formData.img}
                                            onChange={handleInputChange}
                                            placeholder="Enter image URL"
                                            style={{ borderRadius: '8px' }}
                                        />
                                        {formData.img && (
                                            <div style={{ marginTop: '10px', textAlign: 'center' }}>
                                                <img 
                                                    src={formData.img} 
                                                    alt="Product preview" 
                                                    style={{ 
                                                        maxWidth: '200px', 
                                                        maxHeight: '200px',
                                                        borderRadius: '8px',
                                                        border: '2px solid #dee2e6'
                                                    }}
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            3D Model URL
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="model3d"
                                            value={formData.model3d}
                                            onChange={handleInputChange}
                                            placeholder="Enter 3D model URL"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Product Name *
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            placeholder="Enter product name"
                                            required
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Emission (CC)
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="emission"
                                            value={formData.emission}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 150cc"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Engine Displacement
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="displacement"
                                            value={formData.displacement}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 149.5cc"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Price (৳) *
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleInputChange}
                                            placeholder="Enter price"
                                            required
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Max Power
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="maxPower"
                                            value={formData.maxPower}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 14.2 PS @ 8000 rpm"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Max Torque
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="maxTorque"
                                            value={formData.maxTorque}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 13.5 Nm @ 6000 rpm"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Fuel Capacity (L)
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="fuelCapacity"
                                            value={formData.fuelCapacity}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 12"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Seat Height (mm)
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="seatHeight"
                                            value={formData.seatHeight}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 790"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Weight (kg)
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="weight"
                                            value={formData.weight}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 137"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                                            Description
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder="Enter product description..."
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>
                    
                    <Modal.Footer style={{ borderTop: '2px solid #f0f0f0', padding: '20px 30px' }}>
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => {
                                setShowUpdateModal(false);
                                setSelectedProduct(null);
                            }}
                            disabled={loadingProduct}
                            style={{ borderRadius: '10px', fontWeight: '500' }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit"
                            disabled={updating || loadingProduct}
                            style={{ 
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                fontWeight: '600',
                                minWidth: '120px'
                            }}
                        >
                            {updating ? '⏳ Updating...' : '✓ Update Product'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default ManageProducts;